import { isEqual } from "lodash";
import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

const isAnonymous = ref<boolean>(true);
const ratingFeedback = ref<number | undefined>(undefined);
const email = ref<string | undefined>(undefined);
const firstName = ref<string | undefined>(undefined);
const lastName = ref<string | undefined>(undefined);
const title = ref<string | undefined>(undefined);

export const useFilters = () => {
  const route = useRoute();
  const router = useRouter();

  watch(
    () => route.query,
    async (newParams, oldParams) => {
      if (newParams !== oldParams) {
        const newIsAnonymous: boolean = newParams.isAnonymous
          ? `${newParams.isAnonymous}`?.toLowerCase() !== "false"
          : true;
        if (!isEqual(newIsAnonymous, isAnonymous.value)) {
          isAnonymous.value = newIsAnonymous;
        }

        let newRatingFeedback: number | undefined = Number(newParams.ratingFeedback);
        newRatingFeedback = Number.isFinite(newRatingFeedback) ? newRatingFeedback : undefined;
        if (!isEqual(newRatingFeedback, ratingFeedback.value)) {
          ratingFeedback.value = newRatingFeedback;
        }

        const newEmail = Array.isArray(newParams.email)
          ? (newParams.email?.[0] ?? undefined)
          : (newParams.email ?? undefined);
        if (!isEqual(newEmail, email.value)) {
          email.value = newEmail;
        }

        const newFirstName = Array.isArray(newParams.firstName)
          ? (newParams.firstName?.[0] ?? undefined)
          : (newParams.firstName ?? undefined);
        if (!isEqual(newFirstName, firstName.value)) {
          firstName.value = newFirstName;
        }

        const newLastName = Array.isArray(newParams.lastName)
          ? (newParams.lastName?.[0] ?? undefined)
          : (newParams.lastName ?? undefined);
        if (!isEqual(newLastName, lastName.value)) {
          lastName.value = newLastName;
        }

        const newTitle = Array.isArray(newParams.title)
          ? (newParams.title?.[0] ?? undefined)
          : (newParams.title ?? undefined);
        if (!isEqual(newTitle, title.value)) {
          title.value = newTitle;
        }
      }
    },
    { immediate: true },
  );

  const updateIsAnonymous = async (updatedIsAnonymous: boolean) => {
    isAnonymous.value = updatedIsAnonymous;
    await router.push({
      query: {
        ...route.query,
        isAnonymous: `${updatedIsAnonymous}`,
      },
    });
  };

  const updateRatingFeedback = async (updatedRatingFeedback: number) => {
    ratingFeedback.value = updatedRatingFeedback;
    await router.push({
      query: {
        ...route.query,
        ratingFeedback: `${updatedRatingFeedback}`,
      },
    });
  };

  const updateEmail = async (updatedEmail?: string) => {
    email.value = updatedEmail;
    await router.push({
      query: {
        ...route.query,
        email: `${updatedEmail}`,
      },
    });
  };

  const updateFirstName = async (updatedFirstName?: string) => {
    firstName.value = updatedFirstName;
    await router.push({
      query: {
        ...route.query,
        firstName: `${updatedFirstName}`,
      },
    });
  };

  const updateLastName = async (updatedLastName?: string) => {
    lastName.value = updatedLastName;
    await router.push({
      query: {
        ...route.query,
        lastName: `${updatedLastName}`,
      },
    });
  };

  const updateTitle = async (updatedTitle?: string) => {
    title.value = updatedTitle;
    await router.push({
      query: {
        ...route.query,
        title: `${updatedTitle}`,
      },
    });
  };

  const clearFilters = async () => {
    await router.push({
      query: {},
    });
  };

  return {
    isAnonymous,
    updateIsAnonymous,
    ratingFeedback,
    updateRatingFeedback,
    email,
    updateEmail,
    firstName,
    updateFirstName,
    lastName,
    updateLastName,
    title,
    updateTitle,
    clearFilters,
  };
};
